import React, { useState, useEffect } from 'react';
import { 
  Archive, 
  Trash2, 
  RotateCcw, 
  Calendar, 
  FileText, 
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { BackupService } from '../services';
import { JsonFile } from '../types';

interface BackupManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onRestore?: (filename: string) => void;
  backupService: BackupService;
}

export const BackupManager: React.FC<BackupManagerProps> = ({ 
  isOpen, 
  onClose, 
  onRestore,
  backupService
}) => {
  const [backups, setBackups] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedBackup, setExpandedBackup] = useState<string | null>(null);
  const [backupContent, setBackupContent] = useState<JsonFile | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadBackups();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBackups = async () => {
    try {
      setLoading(true);
      setError(null);
      const backupList = await backupService.getBackups();
      setBackups(backupList);
    } catch (err) {
      setError('Error al cargar los backups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBackup = async (filename: string) => {
    try {
      setLoading(true);
      const content = await backupService.getBackup(filename);
      setBackupContent(content);
      setExpandedBackup(expandedBackup === filename ? null : filename);
    } catch (err) {
      setError('Error al cargar el contenido del backup');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async (filename: string) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el backup ${filename}?`)) {
      try {
        setLoading(true);
        await backupService.deleteBackup(filename);
        await loadBackups();
        if (expandedBackup === filename) {
          setExpandedBackup(null);
          setBackupContent(null);
        }
      } catch (err) {
        setError('Error al eliminar el backup');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestoreBackup = async (filename: string) => {
    if (window.confirm(`¿Estás seguro de que quieres restaurar desde ${filename}?`)) {
      try {
        setLoading(true);
        const restoredFile = await backupService.restoreFromBackup(filename);
        if (onRestore) {
          onRestore(restoredFile.filename);
        }
        onClose();
      } catch (err) {
        setError('Error al restaurar el backup');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const getBackupInfo = (filename: string) => {
    const match = filename.match(/^(.+)\.(\d+)\.json$/);
    if (match) {
      return {
        originalName: match[1] + '.json',
        version: parseInt(match[2], 10),
      };
    }
    return {
      originalName: filename,
      version: 0,
    };
  };

  if (!isOpen) return null;

  return (
    <div className="backup-modal-overlay">
      <div className="backup-modal">
        <div className="backup-header">
          <div className="backup-title">
            <Archive size={20} />
            <h3>Gestión de Backups</h3>
          </div>
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {error && (
          <div className="error" style={{ margin: '16px' }}>
            {error}
            <button 
              onClick={() => setError(null)} 
              style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
        )}

        <div className="backup-content">
          {loading && backups.length === 0 ? (
            <div className="loading">
              <div className="spinner" />
              Cargando backups...
            </div>
          ) : backups.length === 0 ? (
            <div className="empty-state">
              <Archive size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <h3>No hay backups disponibles</h3>
              <p>Los backups se crean automáticamente cuando modificas archivos JSON</p>
            </div>
          ) : (
            <div className="backups-list">
              {backups.map((filename) => {
                const info = getBackupInfo(filename);
                const isExpanded = expandedBackup === filename;
                
                return (
                  <div key={filename} className="backup-item">
                    <div 
                      className="backup-item-header"
                      onClick={() => handleViewBackup(filename)}
                    >
                      <div className="backup-item-info">
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <div className="backup-item-details">
                          <div className="backup-item-name">
                            <FileText size={16} />
                            <span>{info.originalName}</span>
                            <span className="backup-version">v{info.version}</span>
                          </div>
                          <div className="backup-item-meta">
                            <Calendar size={12} />
                            <span>Backup #{info.version}</span>
                          </div>
                        </div>
                      </div>
                      <div className="backup-item-actions">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestoreBackup(filename);
                          }}
                          disabled={loading}
                          title="Restaurar este backup"
                        >
                          <RotateCcw size={14} />
                          Restaurar
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBackup(filename);
                          }}
                          disabled={loading}
                          title="Eliminar este backup"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    
                    {isExpanded && backupContent && (
                      <div className="backup-item-content">
                        <div className="backup-preview">
                          <h4>Vista previa del backup:</h4>
                          <div className="backup-preview-info">
                            <p><strong>Archivo:</strong> {backupContent.filename}</p>
                            <p><strong>Última modificación:</strong> {formatDate(backupContent.lastModified)}</p>
                          </div>
                          <pre className="backup-preview-json">
                            {JSON.stringify(backupContent.content, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="backup-footer">
          <div className="backup-help">
            <small>
              <strong>Información:</strong> Los backups se crean automáticamente antes de cada modificación.
              Puedes restaurar cualquier versión anterior o eliminar backups que ya no necesites.
            </small>
          </div>
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
